import { buildPrintHeaderHtml, estilosPrintHeader, getPrintLogoUrl, getPrintEncabezado } from './printBranding';
import { esPagoCuentaCliente, numeroComprobantePago, esMovimientoPagoCuenta } from './comprobantePago';
import { formatFechaSistema, formatFechaHoraSistema } from './fechasSistema';

const formatMonto = (valor) =>
  parseFloat(valor || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const estilosComprobanteBase = `
  html, body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
  }
  ${estilosPrintHeader}
  .hoja-comprobante {
    display: flex;
    flex-direction: column;
    height: 277mm;
    max-height: 277mm;
    box-sizing: border-box;
  }
  .comprobante-copia {
    flex: 1;
    max-height: 50%;
    overflow: hidden;
    box-sizing: border-box;
  }
  .comprobante-copia + .comprobante-copia {
    border-top: 1px dashed #999;
  }
  .recibo-info {
    margin-bottom: 10px;
  }
  .recibo-info h2 {
    margin: 0;
    color: #333;
    font-size: 12px;
  }
  .recibo-info p {
    margin: 4px 0;
    font-size: 9px;
  }
  .cliente-info {
    background: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
    margin-bottom: 10px;
    font-size: 8px;
  }
  .cliente-info h3 {
    margin: 0 0 4px 0;
    font-size: 10px;
  }
  .cliente-info p {
    margin: 2px 0;
  }
  .detalle {
    margin: 8px 0;
  }
  .detalle table {
    width: 100%;
    border-collapse: collapse;
    font-size: 8px;
  }
  .detalle th, .detalle td {
    padding: 4px 6px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }
  .detalle th {
    background: #333;
    color: white;
    font-size: 8px;
  }
  .total {
    text-align: right;
    font-size: 10px;
    font-weight: bold;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #333;
  }
  .total p {
    margin: 2px 0;
  }
  .footer {
    margin-top: 8px;
    text-align: center;
    font-size: 7px;
    color: #666;
  }
  .footer p {
    margin: 2px 0;
  }
  .copia-label {
    text-align: right;
    font-weight: bold;
    color: #555;
    margin-bottom: 4px;
  }
  .anulado-banner {
    background: #fdecea;
    color: #c0392b;
    border: 1px solid #c0392b;
    padding: 6px;
    text-align: center;
    font-weight: bold;
    font-size: 9px;
    margin-bottom: 8px;
  }
  @media print {
    body { margin: 0; padding: 0; }
    .no-print { display: none; }
    .hoja-comprobante { height: 277mm; page-break-after: avoid; }
    .comprobante-copia { page-break-inside: avoid; }
  }
`;

const estilosComprobante = `
  @page { size: A4 portrait; margin: 8mm; }
  body { font-size: 9px; }
  ${estilosComprobanteBase}
  .comprobante-copia {
    padding: 4mm 6mm;
    transform: scale(0.88);
    transform-origin: top center;
  }
  .copia-label { font-size: 10px; }
`;

const estilosComprobanteReciboPago = `
  @page { size: A4 portrait; margin: 4mm; }
  body { font-size: 11px; }
  ${estilosComprobanteBase}
  .comprobante-copia {
    padding: 2mm 3mm;
    transform: none;
  }
  .copia-label { font-size: 11px; }
  .recibo-info h2 { font-size: 14px; }
  .recibo-info p { font-size: 11px; }
  .cliente-info { font-size: 10px; margin-bottom: 6px; padding: 6px; }
  .cliente-info h3 { font-size: 12px; }
  .detalle { margin: 6px 0; }
  .detalle table { font-size: 10px; }
  .detalle th { font-size: 10px; }
  .total { font-size: 12px; margin-top: 6px; padding-top: 6px; }
  .footer { font-size: 9px; margin-top: 6px; }
  .anulado-banner { font-size: 11px; margin-bottom: 6px; }
  .print-empresa-datos { font-size: 11px; }
  .print-empresa-nombre { font-size: 12px; }
  .print-doc-titulo { font-size: 16px; }
  .print-doc-meta { font-size: 11px; }
  .print-header { margin-bottom: 6px; padding-bottom: 6px; }
`;

const filasFormasPagoRecibo = (recibo) => {
  if (recibo.formas_pago && recibo.formas_pago.length > 0) {
    return recibo.formas_pago.map((fp) => `
      <tr>
        <td>${esc(recibo.concepto)}</td>
        <td>
          ${esc(fp.forma_pago_nombre)}
          ${fp.cheque ? `<br/><small>Cheque N° ${esc(fp.cheque.numero_cheque)} - ${esc(fp.cheque.banco_nombre)} - Titular: ${esc(fp.cheque.titular)}</small>` : ''}
          ${fp.cheque_propio ? `<br/><small>Cheque N° ${esc(fp.cheque_propio.numero)}${fp.cheque_propio.cuenta_banco_nombre ? ` - ${esc(fp.cheque_propio.cuenta_banco_nombre)}` : ''}</small>` : ''}
          ${fp.transferencia ? `<br/><small>Transferencia - Cuenta: ${esc(fp.transferencia.numero_cuenta)} - ${esc(fp.transferencia.banco_nombre)}</small>` : ''}
          ${fp.tarjeta ? `<br/><small>Ticket N° ${esc(fp.tarjeta.numero_ticket)}</small>` : ''}
        </td>
        <td style="text-align: right;">$${formatMonto(fp.monto)}</td>
      </tr>
    `).join('');
  }
  return `
    <tr>
      <td>${esc(recibo.concepto)}</td>
      <td>No especificado</td>
      <td style="text-align: right;">$${formatMonto(recibo.monto)}</td>
    </tr>
  `;
};

const filasFormasPagoPago = (pago) => {
  if (pago.formas_pago && pago.formas_pago.length > 0) {
    return pago.formas_pago.map((fp) => `
      <tr>
        <td>${esc(pago.concepto)}</td>
        <td>
          ${esc(fp.forma_pago_nombre)}
          ${fp.cheque_propio ? `<br/><small>Cheque N° ${esc(fp.cheque_propio.numero)}${fp.cheque_propio.cuenta_banco_nombre ? ` - ${esc(fp.cheque_propio.cuenta_banco_nombre)}` : ''}</small>` : ''}
          ${(fp.cheques_cartera || []).map((ch) => `<br/><small>Cheque tercero N° ${esc(ch.numero)} - ${esc(ch.banco_nombre || '')}</small>`).join('')}
          ${fp.transferencia ? `<br/><small>Transferencia - Cuenta: ${esc(fp.transferencia.numero_cuenta)} - ${esc(fp.transferencia.banco_nombre)}</small>` : ''}
        </td>
        <td style="text-align: right;">$${formatMonto(fp.monto)}</td>
      </tr>
    `).join('');
  }
  return `
    <tr>
      <td>${esc(pago.concepto)}</td>
      <td>No especificado</td>
      <td style="text-align: right;">$${formatMonto(pago.monto)}</td>
    </tr>
  `;
};

const filasFormasPagoDetalle = (formasPago, concepto) => {
  if (!formasPago?.length) return '';
  return formasPago.map((fp) => `
    <tr>
      <td>${esc(concepto)}</td>
      <td>
        ${esc(fp.forma_pago_nombre)}
        ${fp.cheque ? `<br/><small>Cheque N° ${esc(fp.cheque.numero_cheque)} - ${esc(fp.cheque.banco_nombre)}</small>` : ''}
        ${fp.cheque_propio ? `<br/><small>Cheque N° ${esc(fp.cheque_propio.numero)}</small>` : ''}
        ${(fp.cheques_cartera || []).map((ch) => `<br/><small>Cheque tercero N° ${esc(ch.numero)}</small>`).join('')}
        ${fp.transferencia ? `<br/><small>Transferencia - ${esc(fp.transferencia.numero_cuenta)}</small>` : ''}
        ${fp.tarjeta ? `<br/><small>Ticket N° ${esc(fp.tarjeta.numero_ticket)}</small>` : ''}
      </td>
      <td style="text-align: right;">$${formatMonto(fp.monto)}</td>
    </tr>
  `).join('');
};

export function buildReciboHtml(recibo, formatFecha, logoUrl, encabezado) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const anulado = recibo.anulado_at
    ? `<div class="anulado-banner">ANULADO${recibo.anulado_at ? ` — ${formatFechaHoraSistema(recibo.anulado_at)}` : ''}</div>`
    : '';

  return `
    ${anulado}
    ${buildPrintHeaderHtml({
      titulo: 'RECIBO DE COBRO',
      logoUrl: logoUrl || getPrintLogoUrl(),
      encabezado: encabezado || getPrintEncabezado(),
      fecha: fmt(recibo.fecha),
      numeroDocumento: recibo.numero_recibo,
    })}
    <div class="cliente-info">
      <h3>Cliente</h3>
      <p><strong>Razón Social:</strong> ${esc(recibo.razon_social)}</p>
      <p><strong>CUIT:</strong> ${esc(recibo.cuit)}</p>
      ${recibo.direccion ? `<p><strong>Dirección:</strong> ${esc(recibo.direccion)}</p>` : ''}
      ${recibo.telefono ? `<p><strong>Teléfono:</strong> ${esc(recibo.telefono)}</p>` : ''}
    </div>
    <div class="detalle">
      <table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Forma de Pago</th>
            <th style="text-align: right;">Monto</th>
          </tr>
        </thead>
        <tbody>
          ${filasFormasPagoRecibo(recibo)}
        </tbody>
      </table>
    </div>
    <div class="total">
      <p>Total: $${formatMonto(recibo.monto)}</p>
      ${recibo.saldo_cuenta != null ? `<p>Saldo restante de la cuenta: $${formatMonto(recibo.saldo_cuenta)}</p>` : ''}
    </div>
    <div class="footer">
      <p>Recibo generado el ${recibo.created_at ? formatFechaHoraSistema(recibo.created_at) : ''}</p>
      ${recibo.usuario_nombre ? `<p>Usuario: ${esc(recibo.usuario_nombre)}</p>` : ''}
    </div>
  `;
}

const filasClientesGrupo = (clientes) => {
  if (!clientes?.length) {
    return '<tr><td colspan="3">Sin clientes asignados</td></tr>';
  }
  return clientes.map((c) => `
    <tr>
      <td>${esc(c.razon_social)}</td>
      <td>${esc(c.cuit)}</td>
      <td style="text-align: right;">$${formatMonto(c.monto_asignado)}</td>
    </tr>
  `).join('');
};

const etiquetaModoReparto = (modo) => (modo === 'orden' ? 'Reparto por orden' : 'Asignación manual');

export function buildReciboGrupoHtml(reciboGrupo, formatFecha, logoUrl, encabezado) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const anulado = reciboGrupo.anulado_at
    ? `<div class="anulado-banner">ANULADO${reciboGrupo.anulado_at ? ` — ${formatFechaHoraSistema(reciboGrupo.anulado_at)}` : ''}</div>`
    : '';

  return `
    ${anulado}
    ${buildPrintHeaderHtml({
      titulo: 'RECIBO DE COBRO GRUPO',
      logoUrl: logoUrl || getPrintLogoUrl(),
      encabezado: encabezado || getPrintEncabezado(),
      fecha: fmt(reciboGrupo.fecha),
      numeroDocumento: reciboGrupo.numero_recibo,
    })}
    <div class="cliente-info">
      <h3>Grupo</h3>
      <p><strong>Nombre:</strong> ${esc(reciboGrupo.grupo_nombre || '—')}</p>
      <p><strong>Modo de reparto:</strong> ${esc(etiquetaModoReparto(reciboGrupo.modo_reparto))}</p>
      <p><strong>Concepto:</strong> ${esc(reciboGrupo.concepto)}</p>
    </div>
    <div class="detalle">
      <p style="font-weight:bold;margin:0 0 4px 0;">Clientes del grupo</p>
      <table>
        <thead>
          <tr>
            <th>Razón Social</th>
            <th>CUIT</th>
            <th style="text-align: right;">Monto asignado</th>
          </tr>
        </thead>
        <tbody>
          ${filasClientesGrupo(reciboGrupo.clientes)}
        </tbody>
      </table>
    </div>
    <div class="detalle">
      <p style="font-weight:bold;margin:0 0 4px 0;">Formas de pago</p>
      <table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Forma de Pago</th>
            <th style="text-align: right;">Monto</th>
          </tr>
        </thead>
        <tbody>
          ${filasFormasPagoRecibo(reciboGrupo)}
        </tbody>
      </table>
    </div>
    <div class="total">
      <p>Total: $${formatMonto(reciboGrupo.monto)}</p>
    </div>
    <div class="footer">
      <p>Recibo generado el ${reciboGrupo.created_at ? formatFechaHoraSistema(reciboGrupo.created_at) : ''}</p>
      ${reciboGrupo.usuario_nombre ? `<p>Usuario: ${esc(reciboGrupo.usuario_nombre)}</p>` : ''}
    </div>
  `;
}

export function buildPagoHtml(pago, formatFecha, logoUrl, encabezado) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const razonSocial = pago.proveedor_razon_social || pago.razon_social || '';
  const cuit = pago.proveedor_cuit || pago.cuit || '';
  const direccion = pago.proveedor_direccion || pago.cliente_direccion || '';
  const telefono = pago.proveedor_telefono || pago.cliente_telefono || '';
  const esPagoCuenta = esPagoCuentaCliente(pago);
  const entidadLabel = esPagoCuenta ? 'Cliente' : 'Proveedor';
  const titulo = esPagoCuenta ? 'PAGO A CUENTA' : 'ORDEN DE PAGO';
  const numeroDocumento = numeroComprobantePago(pago);
  const anulado = pago.anulado_at
    ? `<div class="anulado-banner">ANULADO${pago.anulado_at ? ` — ${formatFechaHoraSistema(pago.anulado_at)}` : ''}</div>`
    : '';

  return `
    ${anulado}
    ${buildPrintHeaderHtml({
      titulo,
      logoUrl: logoUrl || getPrintLogoUrl(),
      encabezado: encabezado || getPrintEncabezado(),
      fecha: fmt(pago.fecha),
      numeroDocumento,
    })}
    <div class="cliente-info">
      <h3>${entidadLabel}</h3>
      <p><strong>Razón Social:</strong> ${esc(razonSocial)}</p>
      <p><strong>CUIT:</strong> ${esc(cuit)}</p>
      ${direccion ? `<p><strong>Dirección:</strong> ${esc(direccion)}</p>` : ''}
      ${telefono ? `<p><strong>Teléfono:</strong> ${esc(telefono)}</p>` : ''}
    </div>
    <div class="detalle">
      <table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Forma de Pago</th>
            <th style="text-align: right;">Monto</th>
          </tr>
        </thead>
        <tbody>
          ${filasFormasPagoPago(pago)}
        </tbody>
      </table>
    </div>
    <div class="total">
      <p>Total: $${formatMonto(pago.monto)}</p>
    </div>
    <div class="footer">
      <p>${esPagoCuenta ? 'Pago' : 'Orden'} generad${esPagoCuenta ? 'o' : 'a'} el ${pago.created_at ? formatFechaHoraSistema(pago.created_at) : ''}</p>
      ${pago.usuario_nombre ? `<p>Usuario: ${esc(pago.usuario_nombre)}</p>` : ''}
    </div>
  `;
}

export function buildMovimientoHtml(mov, formatFecha, { logoUrl, encabezado, recibo, pago } = {}) {
  const fmt = formatFecha || ((f) => formatFechaSistema(f));
  const esProveedor = mov.proveedor_id != null || (mov.numero_pago != null && !mov.recibo_cobro_id && !esMovimientoPagoCuenta(mov));
  const entidadLabel = esProveedor ? 'Proveedor' : 'Cliente';
  const monto = parseFloat(mov.monto || 0);
  const tipoLabel = mov.tipo_movimiento === 'DEBITO' ? 'Débito (Debe)' : 'Crédito (Haber)';
  const formas = recibo?.formas_pago || pago?.formas_pago || [];
  const numeroDoc = mov.numero_recibo || mov.numero_pago || mov.numero_pago_cuenta || mov.id;
  const numeroLabel = mov.numero_recibo
    ? 'N° Recibo'
    : mov.numero_pago_cuenta
      ? 'N° Pago a cuenta'
      : mov.numero_pago
        ? 'N° OP'
        : 'N° interno';
  const formasHtml = formas.length
    ? `
    <div class="detalle">
      <p style="font-weight:bold;margin:0 0 4px 0;">Formas de pago</p>
      <table>
        <thead>
          <tr><th>Concepto</th><th>Forma de Pago</th><th style="text-align:right">Monto</th></tr>
        </thead>
        <tbody>${filasFormasPagoDetalle(formas, mov.concepto)}</tbody>
      </table>
    </div>`
    : '';

  return `
    ${buildPrintHeaderHtml({
      titulo: 'COMPROBANTE DE MOVIMIENTO',
      logoUrl: logoUrl || getPrintLogoUrl(),
      encabezado: encabezado || getPrintEncabezado(),
      fecha: fmt(mov.fecha),
      numeroDocumento: numeroDoc,
      numeroLabel,
    })}
    <div class="cliente-info">
      <h3>${entidadLabel}</h3>
      <p><strong>Razón Social:</strong> ${esc(mov.razon_social)}</p>
      <p><strong>CUIT:</strong> ${esc(mov.cuit)}</p>
      ${mov.movimiento_predefinido_descripcion ? `<p><strong>Tipo:</strong> ${esc(mov.movimiento_predefinido_descripcion)}</p>` : ''}
    </div>
    <div class="detalle">
      <table>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Tipo</th>
            <th style="text-align: right;">Monto</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${esc(mov.concepto)}</td>
            <td>${tipoLabel}</td>
            <td style="text-align: right;">$${formatMonto(monto)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    ${formasHtml}
    <div class="total">
      <p>${tipoLabel}: $${formatMonto(monto)}</p>
      ${mov.saldo_acumulado != null ? `<p>Saldo acumulado: $${formatMonto(mov.saldo_acumulado)}</p>` : ''}
    </div>
    <div class="footer">
      <p>Comprobante generado el ${formatFechaHoraSistema(new Date())}</p>
      ${mov.usuario_nombre ? `<p>Usuario: ${esc(mov.usuario_nombre)}</p>` : ''}
    </div>
  `;
}

function abrirImpresionDual(titulo, buildBody, estilos = estilosComprobante) {
  const ventanaImpresion = window.open('', '_blank');
  const original = buildBody('ORIGINAL');
  const duplicado = buildBody('DUPLICADO');
  const contenido = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${titulo}</title>
      <style>${estilos}</style>
    </head>
    <body>
      <div class="hoja-comprobante">
        <div class="comprobante-copia">
          <div class="copia-label">ORIGINAL</div>
          ${original}
        </div>
        <div class="comprobante-copia">
          <div class="copia-label">DUPLICADO</div>
          ${duplicado}
        </div>
      </div>
    </body>
    </html>
  `;
  ventanaImpresion.document.write(contenido);
  ventanaImpresion.document.close();
  ventanaImpresion.focus();
  setTimeout(() => {
    ventanaImpresion.print();
  }, 250);
}

export function imprimirReciboDual(recibo, formatFecha, logoUrl) {
  abrirImpresionDual(
    `Recibo de Cobro N° ${recibo.numero_recibo}`,
    () => buildReciboHtml(recibo, formatFecha, logoUrl),
    estilosComprobanteReciboPago
  );
}

export function imprimirReciboGrupoDual(reciboGrupo, formatFecha, logoUrl) {
  abrirImpresionDual(
    `Recibo de Cobro Grupo N° ${reciboGrupo.numero_recibo}`,
    () => buildReciboGrupoHtml(reciboGrupo, formatFecha, logoUrl),
    estilosComprobanteReciboPago
  );
}

export function imprimirPagoDual(pago, formatFecha, logoUrl) {
  const esPagoCuenta = esPagoCuentaCliente(pago);
  const numero = numeroComprobantePago(pago);
  const tituloVentana = esPagoCuenta ? `Pago a cuenta N° ${numero}` : `Orden de Pago N° ${numero}`;
  abrirImpresionDual(
    tituloVentana,
    () => buildPagoHtml(pago, formatFecha, logoUrl),
    estilosComprobanteReciboPago
  );
}

export function imprimirMovimientoDual(mov, formatFecha, extras = {}) {
  abrirImpresionDual(
    `Movimiento N° ${mov.id}`,
    () => buildMovimientoHtml(mov, formatFecha, extras)
  );
}
